<template>
  <DeepMenuUser
    v-model="menu"
    v-model:locale="i18n.locale.value"
    :available-locales="i18n.availableLocales"
    color=""
    :is-mobile="device?.isMobile"
    :items="menuItems"
    :user="user"
  >
    <template #prepend-custom>
      <div
        v-if="!AuthService.isAuthenticated && !isDocumentAuthAuthenticated"
        class="pa-2"
      >
        <VBtn
          class="mb-4 w-100"
          color="primary"
          variant="flat"
          @click="createAccount"
        >
          {{
            emailHasAccount
              ? i18n.t('buttons.login')
              : i18n.t('buttons.create_account')
          }}
        </VBtn>
        <VDivider class="mx-2" />
      </div>
    </template>
  </DeepMenuUser>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue';
import { DeepMenuUser } from '@deepcloud/deep-ui-lib';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { DeviceKey } from '@/plugins/device-detector-js.ts';
import AuthService from '@/services/authService.ts';
import { useAppLocale } from '@/composables/useAppLocale.ts';
import { useDocumentAuth } from '@/composables/useDocumentAuth.ts';
import { useOnboarding } from '@/composables/useOnboarding.ts';
import { useDeepAdminStore } from '@/stores/deepadmin/deepadmin.ts';
import { useSignStore } from '@/stores/sign.ts';
import { useSettingsDevStore } from '@/stores/settingsDev.ts';

const { changeLocale, setLocale } = useAppLocale();
const route = useRoute();
const device = inject(DeviceKey);
const documentAuth = useDocumentAuth();
const i18n = useI18n();
const onboarding = useOnboarding();
const signStore = useSignStore();
const settingsDevStore = useSettingsDevStore();
const { emailHasAccount } = useDeepAdminStore();

watch(
  () => i18n.locale.value,
  async (newValue, oldValue) => {
    if (
      newValue !== oldValue &&
      typeof newValue === 'string' &&
      i18n.availableLocales.includes(newValue)
    ) {
      if (AuthService.isAuthenticated) {
        await setLocale(newValue);
      } else {
        changeLocale(newValue);
      }
    }
  }
);

const menu = ref(false);

const user = computed(() => {
  if (AuthService.isAuthenticated) {
    return AuthService?.user
      ? {
          ...AuthService.user,
          displayName:
            AuthService.user?.display_name || AuthService.user?.name || ''
        }
      : {};
  }
  return null;
});

const isDraftMode = computed(
  () => route.name === 'document-create' || route.name === 'p-document-create'
);

const isDocumentAccessRoute = computed(() => {
  return route.name === 'document-access';
});

const isDocumentAuthAuthenticated = computed(() => {
  return documentAuth.isAuthenticated.value;
});

const menuItems = computed(() => {
  return [
    {
      key: 'deep_admin',
      title: i18n.t('buttons.manage_account'),
      dataTestId: 'item-deep-admin',
      show: () => AuthService.isAuthenticated && !isDraftMode.value,
      href: `${import.meta.env.VITE_DEEPADMIN_BASE_URL}account/profile`,
      target: '_blank'
    },
    {
      key: 'language',
      dataTestId: 'user-language',
      title: i18n.t('buttons.language')
    },
    {
      show: () => AuthService.isAuthenticated,
      divider: true,
      space: 'ma-2'
    },
    // DEV-only tool: not translated on purpose (never shown in int/prod).
    {
      key: 'dev-mode',
      title: `DEV mode: ${settingsDevStore.devModeEnabled ? 'enabled' : 'disabled'}`,
      dataTestId: 'item-dev-mode',
      class: settingsDevStore.devModeEnabled ? 'text-success' : 'text-error',
      show: () =>
        AuthService.isAuthenticated && settingsDevStore.isDevModeAllowed,
      action: () => {
        menu.value = false;
        settingsDevStore.devShowSettings = !settingsDevStore.devShowSettings;
      }
    },
    {
      key: 'logout',
      title: i18n.t('buttons.logout'),
      dataTestId: 'item-logout',
      show: () => !isDocumentAccessRoute.value && AuthService.isAuthenticated,
      action: () => logout()
    }
  ];
});

const me = computed(() => {
  return AuthService.isAuthenticated
    ? AuthService.user?.email
    : signStore.signKeyInfo.email;
});

async function logout() {
  try {
    await AuthService.logout({});
  } catch (error) {
    console.error('Error during logout', error);
  }
}

async function createAccount() {
  if (emailHasAccount) {
    const redirectUri = import.meta.env.VITE_BASE_URL + route.fullPath;
    await AuthService.login({
      redirectUri,
      loginHint: me.value
    });
  } else {
    onboarding.openRegisterPage(route.fullPath, me.value);
  }
}
</script>
